import { View, Pressable } from "react-native"
import { useLocalSearchParams, Link, type Href } from "expo-router"

import { Text, ActivityIndicator, Header, List } from "@src/components"
import { priceFromDBFormat, sharedStyles, trpc, type RouterOutputs, dayjs } from "@src/utils"
import { ar } from "@src/i18n"

type MovementItem = RouterOutputs["inventory"]["trackProduct"]["movements"][number]

function formatWeight(grams: number) {
  const sign = grams < 0 ? "-" : ""
  const abs = Math.abs(grams)
  if (abs >= 1000) {
    return `${sign}${(abs / 1000).toFixed(2).replace(/\.00$/, "")} ${ar.kg}`
  }
  return `${sign}${abs} ${ar.gms}`
}

function MovementCard({ item }: { item: MovementItem }) {
  const isPositive = item.quantity > 0
  const isWeight = item.productType === "WEIGHT_PRODUCT" || item.productType === "WEIGHT_DEFINED_PRODUCT"
  const displayQty = isWeight ? formatWeight(Math.abs(item.quantity)) : Math.abs(item.quantity)
  const balanceQty = isWeight ? formatWeight(item.quantityAfter ?? 0) : item.quantityAfter

  const rawTitle = ar[item.type as keyof typeof ar]
  const title = typeof rawTitle === "string" ? rawTitle : item.type
  const description = item.transactionNote ?? null

  const href: Href | null = item.orderId
    ? `/dashboard/orders/${item.orderId}`
    : item.transactionId
      ? `/dashboard/transactions/${item.transactionId}`
      : item.receiptHash
        ? `/dashboard/receipt/hash/${item.receiptHash}`
        : null

  const content = (
    <Pressable className="bg-white border-b border-gray-100 p-4 flex-row justify-between items-start active:opacity-70">
      <View className="flex-1">
        <View className="flex-row items-center gap-2 mb-1">
          <View className={`px-2 py-0.5 rounded text-xs ${isPositive ? "bg-green-100" : "bg-red-100"}`}>
            <Text className={`text-xs font-medium ${isPositive ? "text-green-800" : "text-red-800"}`}>{title}</Text>
          </View>
          <Text className="text-gray-400 text-xs">
            {dayjs.unix(item.createdAt).fromNow()} - {dayjs.unix(item.createdAt).format("DD/MM/YYYY hh:mm A")}
          </Text>
        </View>

        {description ? <Text className="text-gray-600 text-sm mb-1">{description}</Text> : null}

        {item.productBaseId ? <Text className="text-gray-400 text-xs mb-1">{item.productName}</Text> : null}

        <View className="flex-row flex-wrap gap-2 mt-1">
          {item.supplierName ? (
            <Text className="text-gray-500 text-xs">
              {ar.supplier}: {item.supplierName}
            </Text>
          ) : null}

          {/* Standard RESTOCK / RETURN Unit Cost */}
          {item.unitCost != null && item.type !== "SALE" && (
            <Text className="text-gray-500 text-xs">
              {isWeight ? ar.costPerKg : item.type === "RESTOCK" ? ar.boughtAt : ar.unitCost}: {priceFromDBFormat(item.unitCost)} {ar.egp}
            </Text>
          )}

          {/* SALE Unit Cost (COGS) */}
          {item.unitCost != null && item.type === "SALE" && (
            <Text className="text-gray-500 text-xs">
              {isWeight ? ar.costPerKg : ar.unitCost}: {priceFromDBFormat(item.unitCost)} {ar.egp}
            </Text>
          )}

          {/* ACTUAL SOLD PRICE (Revenue) */}
          {item.soldUnitCost != null && item.type === "SALE" && (
            <Text className="font-semibold text-green-700 text-xs">
              {isWeight ? ar.pricePerKg : ar.soldAt}: {priceFromDBFormat(item.soldUnitCost)} {ar.egp}
            </Text>
          )}

          <Text className="text-gray-500 text-xs">
            {ar.qty}: {displayQty}
          </Text>

          {/* Total WAC Value (Cost basis) */}
          {item.totalValue != null && Math.abs(item.totalValue) > 0 && item.type !== "SALE" && (
            <Text className="text-gray-500 text-xs font-semibold">
              {ar.total}: {priceFromDBFormat(Math.abs(item.totalValue))} {ar.egp}
            </Text>
          )}

          {/* Total Sale Revenue */}
          {item.soldTotalRevenue != null && item.type === "SALE" && (
            <Text className="text-green-700 text-xs font-semibold">
              {ar.totalSale}: {priceFromDBFormat(item.soldTotalRevenue)} {ar.egp}
            </Text>
          )}
        </View>
      </View>

      <View className="items-end">
        <Text className={`font-bold text-base ${item.quantity > 0 ? "text-green-600" : item.quantity < 0 ? "text-red-600" : "text-gray-600"}`}>
          {item.quantity > 0 ? "+" : item.quantity < 0 ? "-" : ""}
          {displayQty}
        </Text>
        <Text className="text-gray-400 text-xs">
          {balanceQty} {ar.balance}
        </Text>
      </View>
    </Pressable>
  )

  if (!href) return content
  return (
    <Link href={href} asChild>
      {content}
    </Link>
  )
}

export default function TrackInventoryScreen() {
  const { id, productId } = useLocalSearchParams<{ id: string; productId: string }>()
  const storagePlaceId = Number.parseInt(id, 10)
  const productIdNum = Number.parseInt(productId, 10)
  const query = trpc.inventory.trackProduct.useInfiniteQuery({ productIds: [productIdNum], storagePlaceId, limit: 20 }, { getNextPageParam: (lastPage) => lastPage.nextCursor })

  const movements = query.data?.pages.flatMap((page) => page.movements) || []

  if (query.isLoading) {
    return (
      <>
        <Header title={ar.movementHistory} />
        <ActivityIndicator size="large" color="#4B5563" className="mt-10" />
      </>
    )
  }

  return (
    <>
      <Header title={ar.movementHistory} />
      <List
        data={movements}
        keyExtractor={(item) => `${item.id}`}
        renderItem={({ item }) => <MovementCard item={item} />}
        onEndReached={() => (query.hasNextPage ? query.fetchNextPage() : undefined)}
        onEndReachedThreshold={0.5}
        onRefresh={query.refetch}
        ListFooterComponent={query.isFetchingNextPage ? <ActivityIndicator size="small" className="p-4" /> : null}
        ListEmptyComponent={
          <View className="p-8 items-center">
            <Text className="text-gray-400">{ar.noMovements}</Text>
          </View>
        }
        contentContainerStyle={sharedStyles.contentContainer}
        style={{ flex: 1 }}
      />
    </>
  )
}
